import {useTranslation} from '@/translations/i18n.tsx'
import {Assignment, AssignmentUrlParams} from '@features/assignment/types.ts'
import {AssignmentCardLink} from '@features/assignment/components/assignment-card-link'
import {useGetOfferings} from '@features/assignment/queries/useGetOfferings'
import {useURLParsedParams} from '@hooks/useURLParsedParams'
import {Skeleton} from '@components/ui/skeleton'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon, Image01Icon} from '@components/ui/icon'
import {Separator} from '@components/ui/separator'
import {AxiosError} from 'axios'
import {useState} from 'react'
import {
    ASSIGNMENT_CARD_WRAPPER_CLASSNAME,
    ASSIGNMENT_CARD_TITLE_CLASSNAME,
    ASSIGNMENT_CARD_SUBTITLE_CLASSNAME
} from '@features/assignment/constants'

export const AssignmentOfferingCardImage = ({
    image,
    title,
    subtitle
}: {
    image: string | null
    title: string
    subtitle: string
}) => {
    const [imageState, setImageState] = useState<'loading' | 'error' | 'loaded'>('loading')

    return (
        <div className="grid grid-cols-[56px_minmax(0,1fr)] gap-3">
            <div className="flex items-center justify-center relative aspect-square h-14 w-full rounded-lg bg-neutral-100 text-neutral-400 [&_img]:aspect-square [&_img]:rounded-lg [&_img]:object-cover">
                <img
                    src={image ?? ''}
                    alt={title}
                    hidden={imageState == 'loading' || imageState == 'error'}
                    onError={() => setImageState('error')}
                    onLoad={() => setImageState('loaded')}
                />
                {imageState == 'loading' ? (
                    <Skeleton className="aspect-square w-full rounded-lg object-cover" />
                ) : imageState == 'error' ? (
                    <Image01Icon />
                ) : null}
            </div>

            <div className="flex flex-col gap-1">
                <h3 className="font-medium">{title}</h3>
                <p className="text-sm font-normal text-neutral-700">{subtitle}</p>
            </div>
        </div>
    )
}

export const AssignmentOfferingCard = ({assignment}: {assignment: Assignment}) => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(AssignmentUrlParams)

    const assignmentOfferingsQuery = useGetOfferings(assignment.entity.external_id, {
        enabled: urlParams.success
    })

    const hasOfferings = assignmentOfferingsQuery.data && assignmentOfferingsQuery.data.length > 0

    if (assignmentOfferingsQuery.isPending) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <Skeleton height={28} />
                <div className="flex flex-col gap-4">
                    {Array.from({length: 3}).map((_, index) => (
                        <Skeleton key={index} height={56} />
                    ))}
                </div>
            </div>
        )
    }

    if (
        !urlParams.success ||
        (assignmentOfferingsQuery.error instanceof AxiosError &&
            assignmentOfferingsQuery.error.response?.status == 404) ||
        !assignmentOfferingsQuery.data ||
        !hasOfferings
    ) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>{t('assignment:shootlist_card:title')}</h3>
                <AssignmentCardLink taskCategoryName={assignment.task_category.name} pdfLink={assignment?.pdf_link} />
            </div>
        )
    }

    if (assignmentOfferingsQuery.isError) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
            </div>
        )
    }

    return (
        <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
            <div className="flex flex-col gap-1">
                <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>{t('assignment:shootlist_card:title')}</h3>

                <p className={ASSIGNMENT_CARD_SUBTITLE_CLASSNAME}>{t('assignment:offering_card:description')}</p>
            </div>

            {assignmentOfferingsQuery.data.map(offering => (
                <AssignmentOfferingCardImage
                    key={offering.id}
                    image={offering.mainPhotoUrl}
                    title={offering.title}
                    subtitle={offering.description}
                />
            ))}

            <Separator orientation="horizontal" />

            <AssignmentCardLink taskCategoryName={assignment.task_category.name} pdfLink={assignment?.pdf_link} />
        </div>
    )
}
